/**
 * Amazon Publishing Guide
 * Step-by-step guide to publish on Amazon KDP + monetized services
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Layout } from '../components/Layout';
import { SeoHead } from '../components/SeoHead';
import { SEO } from '../config/seo';
import { 
  BookOpen, Check, DollarSign, ExternalLink, 
  Clock, Target, TrendingUp, Package, Zap
} from 'lucide-react';
import { ServiceRequestModal } from '../components/ServiceRequestModal';

export function AmazonPublishingGuide() {
  const { user } = useAuth();
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  const publishingSteps = [
    {
      number: 1,
      title: "Finalize & Format Your Manuscript",
      duration: "2-5 days",
      cost: "$0-$1,500",
      icon: <BookOpen className="w-6 h-6 text-blue-600" />,
      tasks: [
        {
          name: "Final Edit & Proofread",
          options: [
            "ProWritingAid or Grammarly Premium (DIY)",
            "Hire editor on Reedsy.com ($300-$1,500)",
            "Upwork professional editors"
          ]
        },
        {
          name: "Format Interior",
          details: {
            ebook: "Vellum (Mac, $249) or Free Reedsy Book Editor",
            paperback: "Vellum or Atticus ($147) or Kindle Create (free)",
            trimSizes: ["6×9″ (most fiction)", "5.5×8.5″ (literary/romance)", "6.14×9.21″ (hardcover)"]
          }
        }
      ]
    },
    {
      number: 2,
      title: "Create a Professional Cover",
      duration: "2-5 days",
      cost: "$50-$800",
      icon: <Package className="w-6 h-6 text-purple-600" />,
      description: "Do NOT design it yourself unless you are a graphic designer. Your cover sells the book.",
      services: [
        { name: "100Covers.com", price: "$150-$350", turnaround: "48 hours (fastest)", rating: "⭐⭐⭐⭐⭐" },
        { name: "GetCovers.com", price: "$100-$250", turnaround: "3-5 days", rating: "⭐⭐⭐⭐⭐" },
        { name: "Damonza.com", price: "$400-$800", turnaround: "1-2 weeks (premium)", rating: "⭐⭐⭐⭐⭐" },
        { name: "TheBookCoverDesigner.com", price: "$50-$200", turnaround: "Instant (premade)", rating: "⭐⭐⭐⭐" }
      ],
      tip: "Give designers 5-10 bestselling books in your exact sub-genre - they will nail the look."
    },
    {
      number: 3,
      title: "Set Up Amazon KDP Account",
      duration: "15 minutes",
      cost: "Free",
      icon: <Target className="w-6 h-6 text-green-600" />,
      steps: [
        "Go to https://kdp.amazon.com",
        "Sign in with Amazon account or create new",
        "Fill out tax info (5 minutes)",
        "Non-US authors: Click no SSN and get 30% withholding waived via treaty"
      ]
    },
    {
      number: 4,
      title: "Upload to Kindle Direct Publishing",
      duration: "1-2 hours",
      cost: "Free",
      icon: <TrendingUp className="w-6 h-6 text-orange-600" />,
      sections: [
        {
          type: "eBook (Kindle)",
          steps: [
            "Add title & keyword-rich subtitle",
            "Pick 7 keywords (use Publisher Rocket or Helium10)",
            "Choose 2 BISAC categories (pick smaller ones you can dominate)",
            "Upload manuscript (.docx or .epub)",
            "Upload cover (1600px min height, 2560px ideal, RGB, JPG)",
            "Set price: $2.99-$9.99 for 70% royalty (sweet spot: $4.99)",
            "Enroll in KDP Select (recommended for first 90 days)"
          ]
        },
        {
          type: "Paperback & Hardcover",
          steps: [
            "Click + Create paperback or + Create hardcover",
            "ISBN: Use Amazon free ISBN or buy from Bowker ($125 for 10)",
            "Choose cream/white paper, matte/glossy cover",
            "Set price: $14.99-$19.99 (aim for $3-6 profit after printing)"
          ]
        }
      ]
    },
    {
      number: 5,
      title: "Launch Strategy",
      duration: "Launch week",
      cost: "$50-$500",
      icon: <Zap className="w-6 h-6 text-yellow-600" />,
      strategy: [
        "Pre-orders: Set eBook 30-90 days out (build buzz)",
        "Day 1 price: $0.99 or free 5-day promo (KDP Select)",
        "Run Amazon AMS ads ($50-$200 targeting comp authors)",
        "Post in 20-30 niche Facebook groups, Reddit, TikTok/BookTok",
        "Get 10+ reviews on launch day (triggers Amazon algorithm)"
      ]
    }
  ];

  const optionalAddons = [
    {
      name: "Wide Distribution",
      description: "Publish on Apple Books, Kobo, Barnes & Noble",
      service: "Draft2Digital or PublishDrive (10% commission)",
      benefit: "Reach readers beyond Amazon"
    },
    {
      name: "Audiobook",
      description: "Record via Findaway Voices or ACX (Amazon)",
      cost: "$1,000-$4,000 or royalty share",
      benefit: "Tap into $4B audiobook market"
    },
    {
      name: "Book Marketing",
      description: "BookBub ads, Amazon AMS, Facebook ads",
      cost: "$100-$1,000/month",
      benefit: "Drive consistent sales after launch"
    }
  ];

  const handleServiceRequest = () => {
    if (!user) {
      alert('Please sign in to request publishing service');
      return;
    }
    // Create a placeholder document object for the modal
    setSelectedDocument({
      id: 'new',
      title: 'Your Manuscript',
      doc_type: 'Document',
      word_count: 0
    });
    setShowServiceModal(true);
  };

  return (
    <Layout>
      <SeoHead {...SEO.amazon} />
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-yellow-50 to-green-50">
        <div className="container mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center max-w-4xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 border-2 border-orange-300 rounded-full mb-4">
            <BookOpen className="w-5 h-5 text-orange-600" />
            <span className="font-semibold text-orange-900">From Manuscript to Bestseller</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-orange-600 to-green-600">
            How to Get Your Book on Amazon
          </h1>
          
          <p className="text-xl text-gray-700 mb-6">
            The fastest, most professional way to publish and sell on Amazon in 2025. 
            This is the exact path 95% of successful indie authors use today.
          </p>

          <Alert className="bg-green-50 border-green-300">
            <Check className="w-5 h-5 text-green-600" />
            <AlertDescription className="text-left">
              <strong className="text-green-900">Timeline:</strong> 7-14 days from manuscript to live on Amazon. 
              <strong> Total cost: $600-$2,000</strong> for a 100% professional launch (cover $200 + editing $500-$1,200 + formatting $0-$250).
            </AlertDescription>
          </Alert>
        </div>

        {/* Publishing Steps */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold mb-2 text-center">
            Step-by-Step Publishing Guide
          </h2>
          <p className="text-center text-gray-600 mb-8">
            Do these in order - 7 to 14 days total
          </p>

          <div className="space-y-8">
            {publishingSteps.map((step, idx) => (
              <Card key={idx} className="border-2 border-orange-200 shadow-lg">
                <CardHeader>
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center text-2xl font-bold text-orange-600">
                      {step.number}
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-2xl mb-2">{step.title}</CardTitle>
                      <div className="flex flex-wrap gap-4 text-sm">
                        <div className="flex items-center gap-1 text-gray-600">
                          <Clock className="w-4 h-4" />
                          <span>{step.duration}</span>
                        </div>
                        <div className="flex items-center gap-1 text-gray-600">
                          <DollarSign className="w-4 h-4" />
                          <span>{step.cost}</span>
                        </div>
                      </div>
                    </div>
                    <div className="hidden md:block">
                      {step.icon}
                    </div>
                  </div>
                </CardHeader>

                <CardContent>
                  {step.description && (
                    <p className="text-gray-700 mb-4">{step.description}</p>
                  )}

                  {step.tasks && (
                    <div className="space-y-4">
                      {step.tasks.map((task, i) => (
                        <div key={i} className="bg-blue-50 rounded-lg p-4">
                          <h4 className="font-semibold text-blue-900 mb-2">{task.name}</h4>
                          {task.options && (
                            <ul className="space-y-1">
                              {task.options.map((opt, j) => (
                                <li key={j} className="text-sm text-gray-700 flex items-start gap-2">
                                  <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                  <span>{opt}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                          {task.details && (
                            <div className="space-y-2 text-sm">
                              <div>
                                <span className="font-semibold">eBook:</span> {task.details.ebook}
                              </div>
                              <div>
                                <span className="font-semibold">Paperback:</span> {task.details.paperback}
                              </div>
                              <div>
                                <span className="font-semibold">Trim Sizes:</span>
                                <ul className="ml-4 mt-1">
                                  {task.details.trimSizes.map((size, k) => (
                                    <li key={k} className="text-gray-600">• {size}</li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {step.services && (
                    <div className="space-y-3">
                      {step.services.map((service, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                          <div>
                            <div className="font-semibold text-gray-900">{service.name}</div>
                            <div className="text-sm text-gray-600">{service.turnaround}</div>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-purple-600">{service.price}</div>
                            <div className="text-sm">{service.rating}</div>
                          </div>
                        </div>
                      ))}
                      {step.tip && (
                        <Alert className="bg-yellow-50 border-yellow-300 mt-3">
                          <AlertDescription className="text-sm">
                            💡 <strong>Pro Tip:</strong> {step.tip}
                          </AlertDescription>
                        </Alert>
                      )}
                    </div>
                  )}

                  {step.steps && (
                    <ol className="space-y-2">
                      {step.steps.map((s, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <span className="flex-shrink-0 w-6 h-6 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-xs font-bold">
                            {i + 1}
                          </span>
                          <span className="text-gray-700 pt-0.5">{s}</span>
                        </li>
                      ))}
                    </ol>
                  )}

                  {step.sections && (
                    <div className="space-y-6">
                      {step.sections.map((section, i) => (
                        <div key={i}>
                          <h4 className="font-bold text-orange-600 mb-3">{section.type}</h4>
                          <ol className="space-y-2">
                            {section.steps.map((s, j) => (
                              <li key={j} className="flex items-start gap-2 text-sm">
                                <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                <span className="text-gray-700">{s}</span>
                              </li>
                            ))}
                          </ol>
                        </div>
                      ))}
                    </div>
                  )}

                  {step.strategy && (
                    <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-4">
                      <h4 className="font-bold text-orange-900 mb-3">Launch Week Plan:</h4>
                      <ul className="space-y-2">
                        {step.strategy.map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <Zap className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Assisted Publishing Service */}
        <div className="mb-12">
          <Card className="bg-gradient-to-r from-green-600 to-blue-600 text-white border-0 shadow-2xl">
            <CardContent className="p-8">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-3xl font-bold mb-4">
                    📚 Done-For-You Publishing Service
                  </h3>
                  <p className="text-green-100 mb-6">
                    Skip the learning curve. We will handle everything from formatting to Amazon upload, 
                    so you can focus on writing your next book.
                  </p>
                  
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-2">
                      <Check className="w-5 h-5" />
                      <span>Professional manuscript formatting (eBook + paperback)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-5 h-5" />
                      <span>KDP account setup and optimization</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-5 h-5" />
                      <span>Keyword research and category selection</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-5 h-5" />
                      <span>Complete upload to Amazon (eBook + paperback)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-5 h-5" />
                      <span>Launch strategy consultation</span>
                    </div>
                  </div>

                  <div className="bg-white/10 backdrop-blur rounded-lg p-4 mb-6">
                    <div className="text-2xl font-bold mb-1">$299</div>
                    <div className="text-sm text-green-200">
                      Complete publishing package (does not include cover design or editing)
                    </div>
                  </div>

                  <Button
                    onClick={handleServiceRequest}
                    className="w-full bg-white text-green-600 hover:bg-gray-100"
                    size="lg"
                  >
                    Get Started with Publishing →
                  </Button>
                </div>

                <div className="bg-white/10 backdrop-blur rounded-xl p-6">
                  <h4 className="font-semibold text-lg mb-4">Premium Add-Ons</h4>
                  <ul className="space-y-4 text-green-100 text-sm">
                    <li className="flex items-start gap-2">
                      <div className="flex-shrink-0 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold">+</div>
                      <div>
                        <div className="font-semibold">Cover Design Package</div>
                        <div className="text-xs text-green-200">Professional cover from our partners (+$150-$350)</div>
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="flex-shrink-0 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold">+</div>
                      <div>
                        <div className="font-semibold">Launch Marketing Setup</div>
                        <div className="text-xs text-green-200">Amazon AMS ads + BookBub profile (+$200)</div>
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="flex-shrink-0 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold">+</div>
                      <div>
                        <div className="font-semibold">Wide Distribution</div>
                        <div className="text-xs text-green-200">Apple, Kobo, B&N via Draft2Digital (+$100)</div>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Optional Add-Ons */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold mb-2 text-center">
            Optional Upgrades
          </h2>
          <p className="text-center text-gray-600 mb-8">
            Powerful add-ons to maximize your book sales
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {optionalAddons.map((addon, idx) => (
              <Card key={idx}>
                <CardHeader>
                  <CardTitle className="text-lg">{addon.name}</CardTitle>
                  <CardDescription>{addon.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  {addon.service && (
                    <div className="text-sm text-gray-700 mb-2">
                      <strong>Service:</strong> {addon.service}
                    </div>
                  )}
                  {addon.cost && (
                    <div className="text-sm text-gray-700 mb-2">
                      <strong>Cost:</strong> {addon.cost}
                    </div>
                  )}
                  <div className="flex items-start gap-2 text-sm text-green-700 font-semibold">
                    <Check className="w-4 h-4 mt-0.5" />
                    <span>{addon.benefit}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Key Links */}
        <div className="mb-12">
          <Card>
            <CardHeader>
              <CardTitle>Essential Resources</CardTitle>
              <CardDescription>Direct links to services mentioned in this guide</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <a href="https://kdp.amazon.com" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                  <span className="font-semibold">Amazon KDP</span>
                  <ExternalLink className="w-4 h-4" />
                </a>
                <a href="https://www.reedsy.com" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                  <span className="font-semibold">Reedsy (Editors & Formatters)</span>
                  <ExternalLink className="w-4 h-4" />
                </a>
                <a href="https://100covers.com" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                  <span className="font-semibold">100Covers</span>
                  <ExternalLink className="w-4 h-4" />
                </a>
                <a href="https://www.draft2digital.com" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                  <span className="font-semibold">Draft2Digital (Wide Distribution)</span>
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Card className="max-w-3xl mx-auto border-2 border-orange-300">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold mb-4">
                Ready to Become a Published Author?
              </h3>
              <p className="text-gray-700 mb-6">
                You have written the book. Now let us help you get it into readers hands. 
                Our done-for-you service handles all the technical work so you can focus on marketing and your next book.
              </p>
              
              <div className="space-y-3 mb-6">
                <Button
                  onClick={handleServiceRequest}
                  className="w-full bg-orange-600 hover:bg-orange-700"
                  size="lg"
                >
                  Get Publishing Service ($299) →
                </Button>
                
                <p className="text-sm text-gray-600">
                  Or follow this guide and do it yourself. Either way, your book deserves to be on Amazon!
                </p>
              </div>

              {user && (
                <Link to="/dashboard">
                  <Button variant="outline" className="w-full">
                    Go to Dashboard →
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Service Request Modal */}
      {showServiceModal && user && (
        <ServiceRequestModal
          document={selectedDocument}
          serviceType="publishing"
          onClose={() => setShowServiceModal(false)}
          onSuccess={() => {
            alert('Publishing service request submitted successfully! We will contact you within 24-48 hours.');
            setShowServiceModal(false);
          }}
        />
      )}
      </div>
    </Layout>
  );
}
